import React from "react";
import { IconButton, Typography, Switch, FormControlLabel } from "@mui/material";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import Visibility from "@mui/icons-material/Visibility";
import AppTable from "components/Shared/AppTable";
import { useAuth } from "hooks";
import { useUpdateDocumentByIdMutation } from "store/services/documentApi";
import { toast } from "react-toastify";

const DocumentList = ({
  documents,
  isBusy = false,
  totalDocuments,
  perPage,
  currentPage,
  handlePageChange,
  handlePerRowsChange,
  onEditAction,
  isActive,
}) => {
  const documentList = React.useMemo(() => documents || [], [documents]);
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const canEdit = isCustomer || isVendor || isContractor || isDriver;
  const [updateDocumentById, { isLoading: updatingDocument }] = useUpdateDocumentByIdMutation();
  const { isAccountAdmin } = useAuth();


  const hasDocumentType = React.useMemo(() => {
    return documentList.some(
      (doc) =>
        (doc?.vendorDocumentType && doc?.vendorDocumentType?._id) ||
        (doc?.documentType && doc?.documentType?._id)
    );
  }, [documentList]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleToggleActive = async (document) => {
    const body = {
      _id: document?._id,
      title: document?.title,
      attachments: document?.attachments || [],
      source: document?.source,
      vendorDocumentType: document?.vendorDocumentType,
      documentType: document?.documentType,
      sourceRef: document?.sourceRef,
      isActive: !document?.isActive,
    };
    try {
      await updateDocumentById({ body }).unwrap();
      toast.success("Document status updated successfully!");
    } catch (err) {
      toast.error(err?.data?.message ?? "Couldn't update document status!");
    }
  };

  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        name: "Title",
        selector: (row) => row?.title,
        cell: (row) => (
          <div title={row?.title} className="line-clamp-2">
            {row?.title}
          </div>
        ),
        minWidth: "150px",
      },
    ];

    if (hasDocumentType) {
      baseColumns.push({
        name: "Document Type",
        selector: (row) => row?.vendorDocumentType?.name || row?.documentType?.name,
        cell: (row) => (
          <div>
            {row?.vendorDocumentType?.name || row?.documentType?.name || "N/A"}
          </div>
        ),
        minWidth: "120px",
      });
    }

    baseColumns.push(
      {
        name: "Created By",
        selector: (row) => row?.createdBy?.name,
        cell: (row) => (
          <div>
            <div>{row?.createdBy?.name || "N/A"}</div>
            <Typography variant="caption" color="textSecondary">
              {row?.createdAt ? new Date(row.createdAt).toLocaleString() : "N/A"}
            </Typography>
          </div>
        ),
      },
      {
        name: "Attachment",
        selector: (row) => row?.attachments?.length > 0 ? row?.attachments : [],
        cell: (row) => (
          row?.attachments?.length > 0 ? (
            <div className="flex gap-3">
              {row?.attachments?.map((attachment) => (
                <IconButton
                  key={attachment?._id}
                  size="small"
                  aria-label="View attachment"
                  onClick={() => window.open(attachment?.url, "_blank")}
                >
                  <Visibility color="primary" />
                </IconButton>
              ))}
            </div>
          ) : (
            <span>No attachments</span>
          )
        ),
        minWidth: "350px",
      },
      {
        name: "Updated By",
        selector: (row) => row?.updatedBy?.name,
        cell: (row) => (
          <div>
            <div>{row?.updatedBy?.name || "N/A"}</div>
            <Typography variant="caption" color="textSecondary">
              {row?.updatedBy && row?.updatedAt
                ? new Date(row.updatedAt).toLocaleString()
                : null}
            </Typography>
          </div>
        ),
      }
    );

    // Add actions column only if user can edit
    if (!canEdit) {
      baseColumns.push({
        name: "Actions",
        cell: (row) => (
          <div className="flex gap-2">
            {isAccountAdmin && <FormControlLabel
              control={
                <Switch
                  checked={row?.isActive || false}
                  onChange={() => handleToggleActive(row)}
                  disabled={canEdit || updatingDocument || isActive === false}
                  size="small"
                />
              }
            />}
            <IconButton
              onClick={() => onEditAction(row)}
              size="small"
              disabled={isActive === false}
              aria-label="Edit Document"
            >
              {isAccountAdmin && <ModeEditOutlineTwoToneIcon />}
            </IconButton>

          </div>
        ),
        ignoreRowClick: true,
        right: true,
      });
    }

    return baseColumns;
  }, [hasDocumentType, canEdit, isAccountAdmin, updatingDocument, isActive, handleToggleActive, onEditAction]);

  return (
    <AppTable
      columns={columns}
      data={documentList}
      paginationServer
      paginationPerPage={perPage}
      paginationDefaultPage={currentPage}
      onChangePage={handlePageChange}
      onChangeRowsPerPage={handlePerRowsChange}
      progressPending={isBusy}
      paginationTotalRows={totalDocuments}
    />
  );
};

export default DocumentList;